import { useState } from "react";
import { toast } from "sonner";
import { Mail, Instagram, Youtube } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Eyebrow } from "@/components/Bits";
import { useDialogs } from "@/components/GlobalDialogs";
import { submitContact } from "@/lib/api";

export default function Contact() {
  const { openWaitlist, openCollaborate } = useDialogs();
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await submitContact(form);
      toast.success(res.message);
      setForm({ name: "", email: "", subject: "", message: "" });
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <section className="relative overflow-hidden border-b border-border noise">
        <div className="ytca-container py-16 sm:py-20">
          <Eyebrow>Contact</Eyebrow>
          <h1 className="mt-5 max-w-3xl font-display text-4xl font-semibold tracking-tight sm:text-6xl">Say hello.</h1>
          <p className="mt-6 max-w-2xl text-lg text-muted-foreground">Questions, ideas, or just want to be part of it? We'd love to hear from you.</p>
        </div>
      </section>

      <div className="ytca-container grid gap-10 py-12 lg:grid-cols-[1.2fr_0.8fr]">
        <Card className="rounded-2xl border-border bg-card p-6 sm:p-8">
          <form onSubmit={submit} className="space-y-5">
            <div className="grid gap-5 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="c-name">Name</Label>
                <Input id="c-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Your name" data-testid="contact-name-input" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="c-email">Email</Label>
                <Input id="c-email" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="you@example.com" data-testid="contact-email-input" />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="c-subject">Subject (optional)</Label>
              <Input id="c-subject" value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} placeholder="What's this about?" data-testid="contact-subject-input" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="c-msg">Message</Label>
              <Textarea id="c-msg" required rows={5} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} placeholder="Your message" data-testid="contact-message-input" />
            </div>
            <Button type="submit" disabled={loading} className="rounded-full" data-testid="contact-submit-button">{loading ? "Sending…" : "Send Message"}</Button>
          </form>
        </Card>

        <div className="space-y-6">
          <Card className="rounded-2xl border-border bg-secondary/40 p-6">
            <h3 className="font-display text-lg font-semibold">Other ways to connect</h3>
            <a href="mailto:hello@ytca.art" className="mt-4 inline-flex items-center gap-2 text-sm text-foreground/80 hover:text-primary" data-testid="contact-email-link"><Mail className="h-4 w-4" />hello@ytca.art</a>
            <div className="mt-4 flex gap-3">
              <a href="#" aria-label="Instagram" className="rounded-full border border-border p-2 hover:text-primary"><Instagram className="h-4 w-4" /></a>
              <a href="#" aria-label="YouTube" className="rounded-full border border-border p-2 hover:text-primary"><Youtube className="h-4 w-4" /></a>
            </div>
          </Card>
          <Card className="rounded-2xl border-border bg-card p-6">
            <h3 className="font-display text-lg font-semibold">Be part of it</h3>
            <div className="mt-4 flex flex-col gap-3">
              <Button onClick={() => openWaitlist()} variant="outline" className="rounded-full" data-testid="contact-waitlist-button">Join the Waitlist</Button>
              <Button onClick={openCollaborate} variant="outline" className="rounded-full" data-testid="contact-collaborate-button">Collaborate With Us</Button>
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
}
